import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { OperationsIcon } from 'assets/icons';
import StopPropagation from 'components/StopPropagation/StopPropagation';
import { getEasyBasketEnabled } from 'domains/appContext/AppContext.store';
import {
  ActiveTabParam,
  PARAM_OPERATIONS,
  PreferredTabParam,
  VehicleExplodedViewDetail,
} from 'domains/catalog/Catalog.types';
import { Box, Flex, Icon, sentenceCase, Text } from 'UI';
import { ListItem, PartsListCard } from './CategoriesMenu.styled';

interface CategoriesMenuProps {
  setHighlightedPartNodeId: (index: string | null) => void;
  enlarged: boolean;
  highlightedPartNodeId: string | null;
  pathname: string;
  details: VehicleExplodedViewDetail[] | undefined;
}

const CategoriesMenu = ({
  enlarged,
  highlightedPartNodeId,
  setHighlightedPartNodeId,
  pathname,
  details,
}: CategoriesMenuProps) => {
  const history = useHistory();
  const urlParams = new URLSearchParams(history.location.search);
  const { t } = useTranslation();
  const isEasyBasketEnabled = useSelector(getEasyBasketEnabled);

  const handleOperationsRedirect = (level2: string, level3?: string) => {
    urlParams.set(ActiveTabParam, PARAM_OPERATIONS);
    urlParams.set(PreferredTabParam, PARAM_OPERATIONS);
    const level3NodeId = level3 ? `/${level3}` : '';
    const link = `${pathname}/${level2}${level3NodeId}?${urlParams}`;
    history.push(link);
    scrollTo(0, 0);
  };

  return enlarged ? (
    <>
      <Box width={30} />
      <PartsListCard
        title={
          <Text type="heading4" color="white">
            {t('catalog.parts.category.car_parts.available_categories', 'Available categories')}
          </Text>
        }
      >
        {details?.map(({ index, label, nodeId: partNodeId, operationIds, items }) => {
          const operationNodeIdLink = items?.find((i) => i.operationIds.length > 0)?.nodeId;
          return (
            <ListItem
              key={`parts-list-item-${partNodeId}`}
              id={`parts-list-item-${partNodeId}`}
              $hovered={highlightedPartNodeId === partNodeId}
              onMouseOver={() => {
                setHighlightedPartNodeId(partNodeId);
              }}
              onMouseLeave={() => {
                setHighlightedPartNodeId(null);
              }}
              onClick={() => {
                history.push({
                  pathname: `${pathname}/${partNodeId}`,
                  search: location.search,
                });
              }}
            >
              <Flex justify={'space-between'} size={0}>
                <Text type="base" cursor={'pointer'}>
                  {`${index}. ${sentenceCase(label)}`}
                </Text>
                {!!operationIds?.length && isEasyBasketEnabled && (
                  <StopPropagation>
                    <Icon
                      IconComponent={OperationsIcon}
                      size={24}
                      hoverScale
                      onClick={() => handleOperationsRedirect(partNodeId, operationNodeIdLink)}
                    />
                  </StopPropagation>
                )}
              </Flex>
            </ListItem>
          );
        })}
      </PartsListCard>
    </>
  ) : null;
};

export default CategoriesMenu;
